import React, { useState, useEffect, useCallback } from 'react';
import { Box, Typography, CircularProgress, Snackbar, Alert, Dialog, DialogTitle, DialogContent, DialogActions, Button } from '@mui/material';
import { useNav } from '../App';
import api from '../services/api';
import { C, SP, SZ } from '../theme';

const S = {
  root: {
    minHeight: '100vh', background: C.bgApp,
    padding: `16px ${SP.screenH}px`,
    display: 'flex', flexDirection: 'column', gap: `${SP.md}px`,
    paddingBottom: `${SZ.navHeight + SP.xl}px`,
  },
  topBar: { display: 'flex', alignItems: 'center', gap: `${SP.md}px`, paddingBottom: `${SP.xs}px` },
  backBtn: { fontSize: '20px', cursor: 'pointer', color: C.textMuted, lineHeight: 1, padding: '2px 4px' },
  pageTitle: { fontSize: '22px', fontWeight: 600, color: C.textPrimary, letterSpacing: '-0.3px' },
  card: { background: C.bgCard, border: `0.5px solid ${C.border}`, borderRadius: '14px', overflow: 'hidden' },
  menuRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: `${SP.md}px ${SP.cardPad}px`,
    cursor: 'pointer', borderBottom: `0.5px solid ${C.border}`,
    '&:last-child': { borderBottom: 'none' },
  },
  menuRowLabel: { fontSize: '14px', color: C.textPrimary },
  menuRowSub: { fontSize: '11px', color: C.textMuted, marginTop: '2px' },
  menuChevron: { fontSize: '18px', color: C.textMuted },
  destructiveLabel: { fontSize: '14px', color: C.orange },
  logoutLabel: { fontSize: '14px', color: C.orange, fontWeight: 500 },
  sectionLabel: {
    fontSize: '11px', color: C.textMuted, letterSpacing: '0.8px',
    textTransform: 'uppercase', padding: `${SP.xs}px 0 ${SP.xs}px`,
  },
  statusBadge: (s) => ({
    display: 'inline-flex', alignItems: 'center',
    borderRadius: '99px', padding: '3px 10px',
    fontSize: '11px', fontWeight: 600,
    background: s === 'active' ? `${C.teal}18`
              : s === 'under_review' ? `${C.orange}18`
              : s === 'suspended' ? `${C.orange}18`
              : s === 'pending_deletion' ? `${C.orange}18`
              : `#ff444418`,
    color: s === 'active' ? C.teal
         : s === 'banned' ? '#ff4444'
         : C.orange,
    border: `0.5px solid ${s === 'active' ? C.teal
           : s === 'banned' ? '#ff4444' : C.orange}44`,
  }),
  infoCard: {
    background: C.bgCard, border: `0.5px solid ${C.border}`,
    borderRadius: '14px', padding: `${SP.cardPad}px`,
  },
  infoLabel: { fontSize: '11px', color: C.textMuted, letterSpacing: '0.5px', textTransform: 'uppercase', marginBottom: '4px' },
  infoValue: { fontSize: '14px', color: C.textPrimary, fontWeight: 500 },
  toggleRow: {
    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
    padding: `${SP.md}px ${SP.cardPad}px`,
  },
  toggleLabel: { fontSize: '14px', color: C.textPrimary },
  toggleSub: { fontSize: '11px', color: C.textMuted, marginTop: '2px', maxWidth: '220px' },
  primaryBtn: {
    width: '100%', height: '48px', background: C.yellow, color: C.yellowText,
    border: 'none', borderRadius: '12px', fontSize: '14px', fontWeight: 600,
    cursor: 'pointer', fontFamily: 'inherit',
  },
  destructiveBtn: {
    width: '100%', height: '48px', background: 'transparent', color: C.orange,
    border: `0.5px solid ${C.orange}`, borderRadius: '12px', fontSize: '14px',
    fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit',
  },
  cancelBtn: {
    width: '100%', height: '40px', background: 'transparent', color: C.textMuted,
    border: `0.5px solid ${C.border}`, borderRadius: '12px', fontSize: '13px',
    cursor: 'pointer', fontFamily: 'inherit', marginTop: `${SP.xs}px`,
  },
  warningBox: {
    background: `${C.orange}12`, border: `0.5px solid ${C.orange}44`,
    borderRadius: '12px', padding: `${SP.cardPad}px`,
  },
  warningText: { fontSize: '13px', color: C.orange, lineHeight: 1.5 },
};

function Toggle({ on, onToggle, disabled }) {
  return (
    <Box onClick={() => !disabled && onToggle()}
      sx={{
        width: 44, height: 24, borderRadius: '12px',
        background: on ? C.yellow : C.bgElevated,
        border: `0.5px solid ${on ? C.yellow : C.border}`,
        position: 'relative', cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.5 : 1, transition: 'background 0.2s', flexShrink: 0,
      }}>
      <Box sx={{
        position: 'absolute', top: '3px', left: on ? '23px' : '3px',
        width: 18, height: 18, borderRadius: '50%',
        background: on ? C.yellowText : C.textDisabled,
        transition: 'left 0.2s',
      }} />
    </Box>
  );
}

function Toast({ toast, onClose, navHeight }) {
  return (
    <Snackbar open={toast.open} autoHideDuration={2500} onClose={onClose}
      anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      sx={{ bottom: `${navHeight + 12}px` }}>
      <Alert severity={toast.severity}
        sx={{ background: C.bgElevated, color: C.textPrimary, border: `0.5px solid ${C.border}` }}>
        {toast.msg}
      </Alert>
    </Snackbar>
  );
}

const STATUS_LABELS = {
  active: 'Active',
  under_review: 'Under review',
  suspended: 'Suspended',
  pending_deletion: 'Pending deletion',
  banned: 'Banned',
};

const STATUS_DESC = {
  active: 'Your account is in good standing.',
  under_review: 'Your account is being reviewed by our team. Some features may be limited.',
  suspended: 'Your account has been temporarily suspended. Contact support for details.',
  pending_deletion: 'Your account is scheduled for deletion. You can cancel this below.',
  banned: 'Your account has been permanently banned.',
};

// ═══════════════════════════════════════════════════════════════
export default function Settings() {
  const navigate = useNav();
  const [subpage, setSubpage] = useState(null); // null | 'account' | 'pause' | 'delete'
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [deleteStep, setDeleteStep] = useState(1); // 1=confirm | 2=confirmed
  const [toast, setToast] = useState({ open: false, msg: '', severity: 'success' });

  const showToast = (msg, severity = 'success') => setToast({ open: true, msg, severity });
  const closeToast = () => setToast(t => ({ ...t, open: false }));

  const loadSettings = useCallback(async () => {
    try {
      const r = await api.get('/settings');
      setAccount(r.data.account);
    } catch {
      showToast('Could not load settings', 'error');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  function handleBack() {
    if (subpage) { setSubpage(null); setDeleteStep(1); }
    else navigate('/profile');
  }

  async function handlePauseToggle() {
    setBusy(true);
    try {
      const r = await api.post('/settings/pause');
      setAccount(a => ({ ...a, is_paused: r.data.is_paused ? 1 : 0 }));
      showToast(r.data.is_paused ? 'Account paused' : 'Account reactivated');
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed to update', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function handleDeleteRequest() {
    setBusy(true);
    try {
      const r = await api.post('/settings/delete');
      setAccount(a => ({ ...a, status: 'pending_deletion', deletion_scheduled_for: r.data.deletion_scheduled_for }));
      setDeleteStep(2);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed', 'error');
    } finally {
      setBusy(false);
    }
  }

  async function handleDeleteCancel() {
    setBusy(true);
    try {
      await api.post('/settings/delete/cancel');
      setAccount(a => ({ ...a, status: 'active', deletion_scheduled_for: null }));
      setDeleteStep(1);
      showToast('Deletion cancelled');
      setSubpage(null);
    } catch (err) {
      showToast(err.response?.data?.message || 'Failed', 'error');
    } finally {
      setBusy(false);
    }
  }

  const [logoutDialogOpen, setLogoutDialogOpen] = useState(false);

  function handleLogout() {
    setLogoutDialogOpen(true);
  }

  function confirmLogout() {
    localStorage.removeItem('binko_token');
    navigate('/');
  }

  if (loading) return (
    <Box sx={{ ...S.root, alignItems: 'center', justifyContent: 'center' }}>
      <CircularProgress sx={{ color: C.teal }} size={28} />
    </Box>
  );

  const isPendingDeletion = account?.status === 'pending_deletion';
  const deletionDate = account?.deletion_scheduled_for
    ? new Date(account.deletion_scheduled_for).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })
    : null;

  // ── ACCOUNT STATUS subpage ──────────────────────────────────
  if (subpage === 'account') return (
    <Box sx={S.root}>
      <Box sx={S.topBar}>
        <Box sx={S.backBtn} onClick={handleBack}>‹</Box>
        <Typography sx={S.pageTitle}>Account Status</Typography>
      </Box>

      <Box sx={S.infoCard}>
        <Typography sx={S.infoLabel}>Current status</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: `${SP.sm}px`, mt: '6px' }}>
          <Box sx={S.statusBadge(account?.status)}>
            {STATUS_LABELS[account?.status] || account?.status}
          </Box>
        </Box>
        <Typography sx={{ fontSize: '13px', color: C.textMuted, mt: `${SP.sm}px`, lineHeight: 1.5 }}>
          {STATUS_DESC[account?.status]}
        </Typography>
      </Box>

      {isPendingDeletion && deletionDate && (
        <Box sx={S.warningBox}>
          <Typography sx={S.warningText}>
            Your account is scheduled for deletion on {deletionDate}. All your data will be permanently removed.
          </Typography>
          <button style={{ ...S.primaryBtn, marginTop: '12px' }}
            onClick={handleDeleteCancel} disabled={busy}>
            {busy ? 'Cancelling…' : 'Cancel deletion'}
          </button>
        </Box>
      )}

      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        PaperProps={{
          sx: {
            background: C.bgCard,
            border: `0.5px solid ${C.border}`,
            borderRadius: '12px',
            padding: '8px',
          }
        }}
      >
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 600, color: C.textPrimary }}>
          Log out?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '14px', color: C.textMuted }}>
            You’ll need to verify your phone number to log back in.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialogOpen(false)} sx={{ color: C.textMuted, fontSize: '14px' }}>
            Cancel
          </Button>
          <Button onClick={confirmLogout} sx={{ color: C.yellow, fontWeight: 600, fontSize: '14px' }}>
            Log out
          </Button>
        </DialogActions>
      </Dialog>
      <Toast toast={toast} onClose={closeToast} navHeight={SZ.navHeight} />
    </Box>
  );

  // ── PAUSE subpage ───────────────────────────────────────────
  if (subpage === 'pause') return (
    <Box sx={S.root}>
      <Box sx={S.topBar}>
        <Box sx={S.backBtn} onClick={handleBack}>‹</Box>
        <Typography sx={S.pageTitle}>Pause Account</Typography>
      </Box>

      <Box sx={S.infoCard}>
        <Typography sx={{ fontSize: '13px', color: C.textMuted, lineHeight: 1.6, mb: `${SP.md}px` }}>
          Pausing hides your profile from Browse and stops new matches. Your existing chats and matches are preserved. You can reactivate anytime.
        </Typography>
        <Box sx={S.toggleRow}>
          <Box>
            <Typography sx={S.toggleLabel}>
              {account?.is_paused ? 'Account paused' : 'Account active'}
            </Typography>
            <Typography sx={S.toggleSub}>
              {account?.is_paused ? 'Tap to reactivate' : 'Tap to pause'}
            </Typography>
          </Box>
          <Toggle
            on={!!account?.is_paused}
            onToggle={handlePauseToggle}
            disabled={busy || ['suspended', 'banned'].includes(account?.status)}
          />
        </Box>
      </Box>

      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        PaperProps={{
          sx: {
            background: C.bgCard,
            border: `0.5px solid ${C.border}`,
            borderRadius: '12px',
            padding: '8px',
          }
        }}
      >
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 600, color: C.textPrimary }}>
          Log out?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '14px', color: C.textMuted }}>
            You’ll need to verify your phone number to log back in.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialogOpen(false)} sx={{ color: C.textMuted, fontSize: '14px' }}>
            Cancel
          </Button>
          <Button onClick={confirmLogout} sx={{ color: C.yellow, fontWeight: 600, fontSize: '14px' }}>
            Log out
          </Button>
        </DialogActions>
      </Dialog>
      <Toast toast={toast} onClose={closeToast} navHeight={SZ.navHeight} />
    </Box>
  );

  // ── DELETE subpage ──────────────────────────────────────────
  if (subpage === 'delete') return (
    <Box sx={S.root}>
      <Box sx={S.topBar}>
        <Box sx={S.backBtn} onClick={handleBack}>‹</Box>
        <Typography sx={S.pageTitle}>Delete Account</Typography>
      </Box>

      {isPendingDeletion ? (
        <>
          <Box sx={S.warningBox}>
            <Typography sx={S.warningText}>
              Your account is already scheduled for deletion on {deletionDate}.
            </Typography>
          </Box>
          <button style={S.primaryBtn} onClick={handleDeleteCancel} disabled={busy}>
            {busy ? 'Cancelling…' : 'Cancel deletion — keep my account'}
          </button>
          <button style={S.cancelBtn} onClick={handleBack}>Back to settings</button>
        </>
      ) : deleteStep === 1 ? (
        <>
          <Box sx={S.warningBox}>
            <Typography sx={S.warningText}>
              Deleting your account will permanently remove all your data including matches, chats, and profile after 90 days. You can cancel within 90 days by logging back in.
            </Typography>
          </Box>
          <button style={S.destructiveBtn} onClick={() => setDeleteStep(2)}>
            I understand — continue
          </button>
          <button style={S.cancelBtn} onClick={handleBack}>Cancel</button>
        </>
      ) : (
        <>
          <Box sx={S.warningBox}>
            <Typography sx={S.warningText}>
              Are you sure? This will schedule your account for permanent deletion. You have 90 days to change your mind.
            </Typography>
          </Box>
          <button style={S.destructiveBtn} onClick={handleDeleteRequest} disabled={busy}>
            {busy ? 'Processing…' : 'Yes, delete my account'}
          </button>
          <button style={S.cancelBtn} onClick={() => setDeleteStep(1)}>Go back</button>
        </>
      )}

      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        PaperProps={{
          sx: {
            background: C.bgCard,
            border: `0.5px solid ${C.border}`,
            borderRadius: '12px',
            padding: '8px',
          }
        }}
      >
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 600, color: C.textPrimary }}>
          Log out?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '14px', color: C.textMuted }}>
            You’ll need to verify your phone number to log back in.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialogOpen(false)} sx={{ color: C.textMuted, fontSize: '14px' }}>
            Cancel
          </Button>
          <Button onClick={confirmLogout} sx={{ color: C.yellow, fontWeight: 600, fontSize: '14px' }}>
            Log out
          </Button>
        </DialogActions>
      </Dialog>
      <Toast toast={toast} onClose={closeToast} navHeight={SZ.navHeight} />
    </Box>
  );

  // ── MAIN SETTINGS MENU ──────────────────────────────────────
  return (
    <Box sx={S.root}>
      <Box sx={S.topBar}>
        <Box sx={S.backBtn} onClick={handleBack}>‹</Box>
        <Typography sx={S.pageTitle}>Settings</Typography>
      </Box>

      <Typography sx={S.sectionLabel}>Account</Typography>
      <Box sx={S.card}>
        <Box sx={S.menuRow} onClick={() => setSubpage('account')}>
          <Box>
            <Typography sx={S.menuRowLabel}>Account Status</Typography>
            <Typography sx={S.menuRowSub}>{STATUS_LABELS[account?.status] || '—'}</Typography>
          </Box>
          <Typography sx={S.menuChevron}>›</Typography>
        </Box>
        <Box sx={S.menuRow} onClick={() => setSubpage('pause')}>
          <Box>
            <Typography sx={S.menuRowLabel}>Pause Account</Typography>
            <Typography sx={S.menuRowSub}>{account?.is_paused ? 'Currently paused' : 'Visible to others'}</Typography>
          </Box>
          <Typography sx={S.menuChevron}>›</Typography>
        </Box>
      </Box>

      <Typography sx={S.sectionLabel}>Legal</Typography>
      <Box sx={S.card}>
        <Box sx={S.menuRow} onClick={() => navigate('/legal/terms')}>
          <Typography sx={S.menuRowLabel}>Terms of Service</Typography>
          <Typography sx={S.menuChevron}>›</Typography>
        </Box>
        <Box sx={S.menuRow} onClick={() => navigate('/legal/privacy')}>
          <Typography sx={S.menuRowLabel}>Privacy Policy</Typography>
          <Typography sx={S.menuChevron}>›</Typography>
        </Box>
        <Box sx={S.menuRow} onClick={() => navigate('/legal/game-rules')}>
          <Typography sx={S.menuRowLabel}>Game Rules</Typography>
          <Typography sx={S.menuChevron}>›</Typography>
        </Box>
      </Box>

      <Typography sx={S.sectionLabel}>Support</Typography>
      <Box sx={S.card}>
        <Box sx={S.menuRow} onClick={() => navigate('/support')}>
          <Typography sx={S.menuRowLabel}>Contact Support</Typography>
          <Typography sx={S.menuChevron}>›</Typography>
        </Box>
        <Box sx={S.menuRow} onClick={() => navigate('/support/report')}>
          <Typography sx={S.destructiveLabel}>Report a User</Typography>
          <Typography sx={S.menuChevron}>›</Typography>
        </Box>
      </Box>

      <Typography sx={S.sectionLabel}>Danger zone</Typography>
      <Box sx={S.card}>
        <Box sx={S.menuRow} onClick={() => setSubpage('delete')}>
          <Typography sx={S.destructiveLabel}>Delete Account</Typography>
          <Typography sx={S.menuChevron}>›</Typography>
        </Box>
      </Box>

      <Typography sx={S.sectionLabel}>Session</Typography>
      <Box sx={S.card}>
        <Box sx={S.menuRow} onClick={handleLogout}>
          <Typography sx={S.logoutLabel}>Log out</Typography>
        </Box>
      </Box>

      <Dialog
        open={logoutDialogOpen}
        onClose={() => setLogoutDialogOpen(false)}
        PaperProps={{
          sx: {
            background: C.bgCard,
            border: `0.5px solid ${C.border}`,
            borderRadius: '12px',
            padding: '8px',
          }
        }}
      >
        <DialogTitle sx={{ fontSize: '16px', fontWeight: 600, color: C.textPrimary }}>
          Log out?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: '14px', color: C.textMuted }}>
            You’ll need to verify your phone number to log back in.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setLogoutDialogOpen(false)} sx={{ color: C.textMuted, fontSize: '14px' }}>
            Cancel
          </Button>
          <Button onClick={confirmLogout} sx={{ color: C.yellow, fontWeight: 600, fontSize: '14px' }}>
            Log out
          </Button>
        </DialogActions>
      </Dialog>
      <Toast toast={toast} onClose={closeToast} navHeight={SZ.navHeight} />
    </Box>
  );
}
